import { Linkrole } from 'app/api/tag-queries/ai-tagging';
import { PresentationTreeResponse, LinkroleOverride } from 'app/api/tag-queries/types';
import { formatDefinition } from 'app/utils/tag-utils';
import { getTagLabel } from 'app/utils/tag-utils/getTagLabel';
import { linkroleLabel } from './LinkroleCell';

/** U+241F SYMBOL FOR UNIT SEPARATOR — cannot occur in a role URL or a concept UUID. */
export const TOKEN_SEP = '␟';

export interface FilterToken {
  roleURI: string;
  nodeId?: string;
}

export function parseToken(token: string): FilterToken {
  const i = token.indexOf(TOKEN_SEP);
  if (i === -1) return { roleURI: token };
  return { roleURI: token.slice(0, i), nodeId: token.slice(i + TOKEN_SEP.length) };
}

export function serializeToken(token: FilterToken): string {
  return token.nodeId ? `${token.roleURI}${TOKEN_SEP}${token.nodeId}` : token.roleURI;
}

/**
 * Per role: conceptName (== presentation node id, the concept qname) -> set of its ancestor node
 * ids in that role. The presentation tree (`Taxonomy.presentationLinks`) keys nodes by concept
 * **name/qname**, matching `AiPrediction.conceptName` — NOT the DB uuid `conceptId`.
 */
export type AncestorIndex = Map<string, Map<string, Set<string>>>;

export function buildAncestorIndex(tree: PresentationTreeResponse | null | undefined): AncestorIndex {
  const index: AncestorIndex = new Map();
  const networks = tree?.presentationTree?.networks ?? [];
  for (const net of networks) {
    const roleURI = net.linkrole?.roleURI;
    if (!roleURI) continue;
    const perRole = index.get(roleURI) ?? new Map<string, Set<string>>();
    const walk = (nodes: { id: string; children?: any[] }[], ancestors: string[]) => {
      for (const node of nodes) {
        if (node?.id == null) continue;
        const set = perRole.get(node.id) ?? new Set<string>();
        ancestors.forEach((a) => set.add(a));
        perRole.set(node.id, set);
        if (node.children?.length) walk(node.children, [...ancestors, node.id]);
      }
    };
    walk(net.roots ?? [], []);
    index.set(roleURI, perRole);
  }
  return index;
}

/**
 * Sub-level-aware predicate: keep a prediction's link-role row for a role iff there is no
 * filter, a whole-ELR token for that role is selected, or a selected sub-node token is an
 * ancestor of the concept in that role. A role absent from the index still matches a
 * whole-ELR selection (never over-hide on source skew).
 *
 * `conceptName` is the concept **qname** (`AiPrediction.conceptName`) — the presentation tree keys
 * nodes by name, not by the DB uuid `conceptId`.
 */
export function keepLinkroleForConcept(lr: Linkrole, conceptName: string, tokens: FilterToken[], ancestorIndex: AncestorIndex): boolean {
  if (!tokens.length) return true;
  const forRole = tokens.filter((t) => t.roleURI === lr.uri);
  if (!forRole.length) return false;
  if (forRole.some((t) => !t.nodeId)) return true; // whole-ELR selection wins
  const ancestors = ancestorIndex.get(lr.uri)?.get(conceptName);
  if (!ancestors) return false;
  return forRole.some((t) => t.nodeId != null && ancestors.has(t.nodeId));
}

interface ResolvedNet {
  roleURI: string;
  code: string;
  elrLabel: string;
  searchBase: string; // "<code> <elr label>" — reused as the child searchText suffix
  roots: any[];
}

/** Visible presentation networks, resolved to labels/code and sorted numerically by ELR code. */
function resolveSortedNets(tree: PresentationTreeResponse | null | undefined, overrides: Record<string, LinkroleOverride>, tagLanguage: string): ResolvedNet[] {
  const networks = tree?.presentationTree?.networks ?? [];
  const seen = new Set<string>();
  const nets: ResolvedNet[] = [];
  for (const net of networks) {
    const roleURI = net.linkrole?.roleURI;
    if (!roleURI || seen.has(roleURI) || overrides[roleURI]?.hidden) continue;
    seen.add(roleURI);
    const definition = net.linkrole.definition ?? '';
    const languageLabel = net.linkrole.labels?.[tagLanguage] ?? net.linkrole.labels?.en;
    const { category } = formatDefinition(definition, languageLabel);
    const elrLabel = linkroleLabel({ uri: roleURI, definition, labels: net.linkrole.labels ?? {} }, { tagLanguage, alias: overrides[roleURI]?.alias });
    nets.push({ roleURI, code: category, elrLabel, searchBase: `${category} ${elrLabel}`, roots: net.roots ?? [] });
  }
  nets.sort((a, b) => {
    const na = parseInt(a.code, 10);
    const nb = parseInt(b.code, 10);
    const aNum = !isNaN(na);
    const bNum = !isNaN(nb);
    if (aNum && bNum && na !== nb) return na - nb;
    if (aNum !== bNum) return aNum ? -1 : 1;
    return a.elrLabel.localeCompare(b.elrLabel);
  });
  return nets;
}

/** A node in the sub-level filter tree: an ELR root or a non-leaf concept. Leaves are excluded. */
export interface LinkroleTreeNode {
  value: string; // serialized FilterToken (bare roleURI for an ELR, roleURI␟nodeId for a concept)
  label: string; // plain label — the tree nests, so no indentation is baked in
  searchText: string; // own name + the ELR code/text, so an ELR search reveals its descendants
  children: LinkroleTreeNode[];
}

/**
 * Build the collapsible sub-level filter forest: one root per visible ELR (numeric-sorted),
 * carrying its **non-leaf** descendant concepts (leaves left out), each in presentation order.
 * A non-leaf whose children are all leaves ends up with an empty `children` (selectable, not
 * expandable). Selecting a node covers its whole subtree via the ancestor index.
 */
export function buildLinkroleTree(
  tree: PresentationTreeResponse | null | undefined,
  overrides: Record<string, LinkroleOverride>,
  tagLanguage: string
): LinkroleTreeNode[] {
  const buildNodes = (nodes: any[], roleURI: string, searchBase: string): LinkroleTreeNode[] =>
    [...nodes]
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .filter((node) => node?.id != null && node.children?.length) // non-leaf only
      .map((node) => {
        const name = getTagLabel({ languages: node.labels as any, currentLang: tagLanguage as any }) || node.id;
        return {
          value: serializeToken({ roleURI, nodeId: node.id }),
          label: name,
          searchText: `${name} ${searchBase}`,
          children: buildNodes(node.children, roleURI, searchBase),
        };
      });
  return resolveSortedNets(tree, overrides, tagLanguage).map((net) => ({
    value: net.roleURI,
    label: net.elrLabel,
    searchText: net.searchBase,
    children: buildNodes(net.roots, net.roleURI, net.searchBase),
  }));
}

/** All selectable token values in a forest (used to drop stale stored tokens on prefill). */
export function collectTreeTokens(forest: LinkroleTreeNode[]): string[] {
  const out: string[] = [];
  const walk = (nodes: LinkroleTreeNode[]) =>
    nodes.forEach((n) => {
      out.push(n.value);
      walk(n.children);
    });
  walk(forest);
  return out;
}

export type NodeCheckState = 'covered' | 'checked' | 'indeterminate' | 'unchecked';

function hasSelectedDescendant(node: LinkroleTreeNode, selected: Set<string>): boolean {
  return node.children.some((c) => selected.has(c.value) || hasSelectedDescendant(c, selected));
}

/**
 * Check-state for a tree row. `ancestorSelected` = some ancestor's token is selected, so this node
 * is **covered** (rendered checked + locked; the parent token already includes it). Otherwise the
 * node is checked if its own token is selected, indeterminate if a descendant is, else unchecked.
 */
export function nodeCheckState(node: LinkroleTreeNode, selected: Set<string>, ancestorSelected: boolean): NodeCheckState {
  if (ancestorSelected) return 'covered';
  if (selected.has(node.value)) return 'checked';
  if (hasSelectedDescendant(node, selected)) return 'indeterminate';
  return 'unchecked';
}

export function prefillSelection(stored: string[] | undefined, validValues: string[]): string[] {
  if (!stored?.length) return [];
  const valid = new Set(validValues);
  return stored.filter((token) => valid.has(token));
}
